import React, { useState, useEffect } from 'react';
import SysAdminTenantDropdownSelectorComponent from '../components/sysadmin-tenant-dropdown-selector.component';
import { useTenantGet } from '../hooks/use-tenant.hook';
import { useActiveTenant } from '../../../../../states/active-tenant.atom';
import { useTranslate } from '../../../../../hooks/use-translate.hook';
import { useBrandingColors } from '../../../../../states/theme.atom';
import {
  defaultPrimaryColor,
  defaultSecondaryColor,
} from '../../../../../const/default-branding-colors.const';
import { useAuthUser } from '../../../../../states/user.atom';
import { activateTenant } from '@utils/tenant.util';

const SysAdminTenantDropdownSelectorContainer: React.FC = () => {
  const { setActiveTenant, activeTenant } = useActiveTenant();
  const [selectedTenantId, setSelectedTenantId] = useState<string | undefined>(undefined);
  const { changeLanguage } = useTranslate();
  const { setBrandingColors } = useBrandingColors();
  const { isSysAdmin } = useAuthUser();
  const [hasRestoredTenant, setHasRestoredTenant] = useState(false);

  // Fetch tenant data when a tenant is selected
  const { data: tenantData, isLoading, error } = useTenantGet(selectedTenantId!);

  // Restore tenant from localStorage on mount and re-activate it (apply branding, language, etc.)
  useEffect(() => {
    if (!hasRestoredTenant && activeTenant && isSysAdmin()) {
      // Re-activate the tenant to apply branding colors and language
      activateTenant(activeTenant, {
        setActiveTenant,
        setBrandingColors,
      });
      setHasRestoredTenant(true);
    }
  }, [activeTenant, hasRestoredTenant, isSysAdmin, setActiveTenant, setBrandingColors]);

  const handleTenantSelect = (tenantId: string | null) => {
    if (!tenantId) {
      // If tenant is cleared, reset active tenant and branding
      setActiveTenant(null);
      setBrandingColors({
        primary: defaultPrimaryColor,
        secondary: defaultSecondaryColor,
      });
      setSelectedTenantId(undefined);
      return;
    }

    setSelectedTenantId(tenantId);
  };

  // Update active tenant when data is loaded
  useEffect(() => {
    if (tenantData?.data && !isLoading && !error) {
      // Replace the tenant activation logic with the utility function
      activateTenant(tenantData.data, {
        setActiveTenant,
        setBrandingColors,
      });

      setSelectedTenantId(undefined); // Reset to avoid unnecessary re-fetches
    }
  }, [tenantData, isLoading, error, setActiveTenant, changeLanguage, setBrandingColors]);

  if (!isSysAdmin()) {
    return null;
  }

  return (
    <SysAdminTenantDropdownSelectorComponent
      onTenantSelect={handleTenantSelect}
      selectedTenant={activeTenant ?? undefined}
      disabled={isLoading}
    />
  );
};

export default SysAdminTenantDropdownSelectorContainer;
